import { analyticsLogger } from '@commander/logger';
import { redactSensitiveUrl } from '@commander/utils';
import mixpanel, { type Config } from 'mixpanel-browser';
import type { AnalyticsConfig, AnalyticsProvider } from '../types';

/** Page URLs Mixpanel adds to every event, `$identify` included. */
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_referrer'] as const;

/** Mixpanel's last step before sending an event: redact sensitive query values from its URLs. */
function redactEventUrls(data: { properties?: Record<string, unknown> }) {
  const properties = data.properties;
  if (!properties) return data;
  for (const key of URL_PROPERTIES) {
    const value = properties[key];
    if (typeof value === 'string') properties[key] = redactSensitiveUrl(value);
  }
  return data;
}

/**
 * Mixpanel analytics provider implementation
 */
export class MixpanelProvider implements AnalyticsProvider {
  private initialized = false;
  private config: AnalyticsConfig = {};

  init(config: AnalyticsConfig): void {
    // Guard against SSR environments
    if (typeof window === 'undefined') {
      analyticsLogger.warn('Cannot initialize in SSR environment');
      return;
    }

    // Store config
    this.config = config;

    // Skip initialization if disabled or no token
    if (config.disabled || !config.token) {
      analyticsLogger.warn('Analytics disabled or no token provided');
      return;
    }

    try {
      // Initialize Mixpanel
      mixpanel.init(config.token, {
        api_host: config.apiHost,
        debug: config.debug ?? false,
        track_pageview: false, // We'll track page views manually
        persistence: 'localStorage',
        // Not in the bundled typings; see mixpanel-browser's `_run_hook`.
        hooks: { before_send_events: redactEventUrls },
      } as Partial<Config>);

      this.initialized = true;

      if (config.debug) {
        analyticsLogger.debug('Mixpanel initialized successfully');
      }
    } catch (error) {
      analyticsLogger.error('Failed to initialize Mixpanel:', error);
    }
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.initialized || this.config.disabled) {
      if (this.config.debug) {
        analyticsLogger.debug(
          'Track skipped (not initialized):',
          eventName,
          JSON.stringify(properties)
        );
      }
      return;
    }

    try {
      mixpanel.track(eventName, properties);

      if (this.config.debug) {
        analyticsLogger.debug('Tracked:', eventName, JSON.stringify(properties));
      }
    } catch (error) {
      analyticsLogger.error('Failed to track event:', eventName, String(error));
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.initialized || this.config.disabled) {
      if (this.config.debug) {
        analyticsLogger.debug('Identify skipped (not initialized):', userId);
      }
      return;
    }

    try {
      mixpanel.identify(userId);

      if (traits) {
        mixpanel.people.set(traits);
      }

      if (this.config.debug) {
        analyticsLogger.debug('Identified user:', userId, JSON.stringify(traits));
      }
    } catch (error) {
      analyticsLogger.error('Failed to identify user:', userId, String(error));
    }
  }

  reset(): void {
    if (!this.initialized || this.config.disabled) {
      return;
    }

    try {
      mixpanel.reset();

      if (this.config.debug) {
        analyticsLogger.debug('Reset complete');
      }
    } catch (error) {
      analyticsLogger.error('Failed to reset:', error);
    }
  }
}
